import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import {
  ArrowRight,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  KanbanSquare,
  Menu,
  MessageSquare,
  Monitor,
  Play,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Tablet,
  X,
  Zap,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useDocumentMeta } from '@/hooks/useDocumentMeta'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/lib/AuthProvider'

export const Route = createFileRoute('/')({
  component: LandingPage,
})

type Plan = {
  id: string
  name: string
  description: string
  price: number
  features_json: string[]
}

const defaultPlans: Plan[] = [
  {
    id: 'basico',
    name: 'Básico',
    description: 'Para lava-rápidos que estão profissionalizando a operação.',
    price: 97,
    features_json: [
      'Painel Kanban de OS',
      'Controle de Caixa Simples',
      'Cadastro de Clientes e Veículos',
      'Emissão de Recibos Térmicos',
      'Suporte via Email',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Para estéticas automotivas de alto fluxo e exigência.',
    price: 147,
    features_json: [
      'Tudo do plano Básico',
      'Leitura OCR de Placas por Câmera',
      'Caixa Cego (Auditoria Antifraude)',
      'Módulo de Comissões por Profissional',
      'Disparos de Notificação WhatsApp',
      'Suporte Prioritário no WhatsApp',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Para centros automotivos e redes que tomam decisões por dados.',
    price: 297,
    features_json: [
      'Tudo do plano Pro',
      'AI Growth Analytics & Tendências',
      'Múltiplas Filiais / Unidades',
      'Pesquisas de Satisfação (NPS)',
      'Acesso API & Integrações',
      'Gerente de Conta Dedicado',
    ],
  },
]

const TESTIMONIALS = [
  {
    name: 'Carlos Henrique Viana',
    role: 'Proprietário',
    shop: 'Studio Detailer Pro • Campinas, SP',
    metric: '+R$ 3.400/mês',
    highlight: 'Fim dos furos de caixa',
    quote:
      'O Caixa Cego acabou com as divergências no fim do dia. O funcionário não vê o saldo da gaveta, apenas declara o que contou. Só aí estancamos mais de R$ 100 de furos por semana.',
    initials: 'CH',
  },
  {
    name: 'Rodrigo Alcantara',
    role: 'Gestor Operacional',
    shop: 'Lava Jato & Estética Imperial • Curitiba, PR',
    metric: '10s por Entrada',
    highlight: 'OCR Imbatível',
    quote:
      'Antes era uma fila de carros na entrada com recepcionista anotando placa no papel. Com o OCR, apontamos a câmera do celular na rampa e o cadastro do carro já abre na hora.',
    initials: 'RA',
  },
  {
    name: 'Marcos Vinícius',
    role: 'Fundador',
    shop: 'Elite Car Care • Belo Horizonte, MG',
    metric: 'Zero Conflitos',
    highlight: 'Comissões Transparentes',
    quote:
      'A equipe de lavagem agora consulta no celular exatamente quanto produziu no dia. Acabaram as dúvidas e desconfianças de comissão no sábado à tarde.',
    initials: 'MV',
  },
]

const FAQ_ITEMS = [
  {
    q: 'Preciso de equipamentos caros ou computadores potentes para usar?',
    a: 'Não! O AutoWash Pro é 100% em nuvem e funciona direto no navegador de qualquer smartphone Android ou iPhone, tablet ou notebook simples. Você não precisa baixar nada nem instalar servidores locais.',
  },
  {
    q: 'Como funciona a Leitura OCR de Placas na prática?',
    a: 'Basta abrir a tela de nova OS no seu celular e apontar a câmera para a placa Mercosul ou cinza. A inteligência visual lê os caracteres em milissegundos, pesquisa no histórico de clientes e preenche os dados do carro instantaneamente.',
  },
  {
    q: 'O que é o Caixa Cego e por que ele protege meu faturamento?',
    a: 'Diferente de caixas tradicionais onde o operador vê o total esperado na tela, no Caixa Cego o funcionário apenas conta e digita o dinheiro/PIX presente. Se houver qualquer divergência, o sistema gera alerta imediato para o gestor sem permitir alterações silenciosas.',
  },
  {
    q: 'O teste grátis exige cartão de crédito no momento do cadastro?',
    a: 'Não! Você pode iniciar o seu período de teste grátis de 7 dias sem digitar nenhum cartão de crédito. Se gostar e decidir continuar, escolhe o plano que melhor se adapta à sua operação.',
  },
  {
    q: 'Existe contrato de fidelidade ou multa rescisória?',
    a: 'Nenhum. Nossos planos são mensais ou anuais sem contratos de fidelidade compulsórios. Você é livre para cancelar a qualquer momento diretamente no seu painel com 1 clique.',
  },
  {
    q: 'Como recebo suporte se tiver alguma dúvida durante o uso?',
    a: 'Nosso suporte é humanizado e atende diretamente via WhatsApp de segunda a sábado. Além disso, o sistema conta com tutoriais interativos e central de ajuda dentro de cada módulo.',
  },
]

// SpotlightCard inspirado na skill Antigravity (TonsuraPro)
function SpotlightCard({
  children,
  className = '',
  spotlightColor = 'rgba(59, 130, 246, 0.12)',
}: {
  children: React.ReactNode
  className?: string
  spotlightColor?: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ x: 50, y: 50 })
  const [hovered, setHovered] = useState(false)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    setPos({ x, y })
  }

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/60 backdrop-blur-xl transition-all duration-300 ${className}`}
      style={{
        background: hovered
          ? `radial-gradient(circle at ${pos.x}% ${pos.y}%, ${spotlightColor} 0%, rgba(15, 23, 42, 0.75) 60%)`
          : undefined,
      }}
    >
      {hovered && (
        <div
          className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-300"
          style={{
            background: `radial-gradient(circle at ${pos.x}% ${pos.y}%, ${spotlightColor.replace('0.12', '0.22')} 0%, transparent 65%)`,
            borderRadius: 'inherit',
          }}
        />
      )}
      <div className="relative z-10">{children}</div>
    </div>
  )
}

function LandingPage() {
  useDocumentMeta({
    title: 'AutoWash Pro - Gestão Inteligente para Estética Automotiva',
    description:
      'O sistema definitivo para lava-rápidos e estéticas automotivas. Controle pátio Kanban, caixa cego antifraude, leitura OCR de placa e comissões.',
  })

  const { user, isLoading, hasOnboarded } = useAuth()
  const [plans, setPlans] = useState<Plan[]>(defaultPlans)
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  // Abas do Mockup Interativo no Hero
  const [heroTab, setHeroTab] = useState<'ocr' | 'kanban' | 'caixa' | 'whatsapp'>('ocr')

  // Sliders da Calculadora de ROI
  const [carsPerDay, setCarsPerDay] = useState<number>(18)
  const [avgTicket, setAvgTicket] = useState<number>(85)
  const [workingDays, setWorkingDays] = useState<number>(26)

  // Cálculos do simulador de ROI
  const roiData = useMemo(() => {
    const monthlyCars = carsPerDay * workingDays
    const grossRevenue = monthlyCars * avgTicket
    // Estimativa conservadora de 12% a mais de faturamento recuperado
    const recoveredRevenue = grossRevenue * 0.12
    const hoursSavedMonth = Math.round(monthlyCars * 0.15) // ~9 min economizados por carro

    return {
      monthlyCars,
      grossRevenue,
      recoveredRevenue,
      hoursSavedMonth,
    }
  }, [carsPerDay, avgTicket, workingDays])

  useEffect(() => {
    async function loadPlans() {
      const { data, error } = await supabase
        .from('system_plans')
        .select('id, name, description, price, features_json')
        .eq('status', 'ACTIVE')
        .order('price', { ascending: true })
      if (data && !error && data.length > 0) {
        setPlans(data as Plan[])
      }
    }
    loadPlans()
  }, [])

  // Redirecionamento se já logado
  if (!isLoading && user && hasOnboarded !== null) {
    if (user.user_metadata?.role === 'SUPERADMIN') {
      return <Navigate to="/master" />
    }
    if (hasOnboarded === false) return <Navigate to="/onboarding" />
    return <Navigate to="/app" />
  }

  const getDisplayPrice = (basePrice: number) => {
    if (billingPeriod === 'yearly') {
      return Math.round(basePrice * 0.8)
    }
    return basePrice
  }

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 font-sans selection:bg-blue-500/30 overflow-x-hidden">
      {/* Navbar Superior */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-[#020617]/75 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 shrink-0">
            <img
              src="/logo.webp"
              alt="AutoWash Pro Logo"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain drop-shadow-[0_0_12px_rgba(59,130,246,0.3)]"
            />
            <div className="flex flex-col">
              <span className="font-extrabold text-base sm:text-xl tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-blue-400">
                AutoWash Pro
              </span>
              <span className="text-[10px] text-blue-400 font-semibold tracking-wider uppercase hidden sm:block">
                Gestão Automotiva Inteligente
              </span>
            </div>
          </div>

          {/* Links Desktop */}
          <div className="hidden lg:flex items-center gap-8 text-sm font-medium text-slate-300">
            <a href="#recursos" className="hover:text-blue-400 transition-colors">
              Recursos
            </a>
            <a href="#simulador" className="hover:text-blue-400 transition-colors">
              Calculadora ROI
            </a>
            <a href="#solucao" className="hover:text-blue-400 transition-colors">
              Comparativo
            </a>
            <a href="#depoimentos" className="hover:text-blue-400 transition-colors">
              Resultados
            </a>
            <a href="#precos" className="hover:text-blue-400 transition-colors">
              Preços
            </a>
            <a href="#faq" className="hover:text-blue-400 transition-colors">
              FAQ
            </a>
          </div>

          {/* Botões de Ação */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-800/60 font-medium text-xs sm:text-sm px-3 sm:px-4 h-9 sm:h-10"
              >
                Entrar
              </Button>
            </Link>
            <Link to="/sandbox" className="hidden sm:block">
              <Button
                variant="outline"
                size="sm"
                className="border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-200 font-medium text-xs sm:text-sm h-9 sm:h-10 rounded-xl"
              >
                <Play className="w-3.5 h-3.5 mr-1.5 text-blue-400 fill-current" />
                Ver Demonstração
              </Button>
            </Link>
            <a href="/login?trial=true">
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold shadow-lg shadow-blue-500/25 rounded-xl px-3 sm:px-5 text-xs sm:text-sm h-9 sm:h-10 whitespace-nowrap"
              >
                Teste 7 Dias Grátis
              </Button>
            </a>
            {/* Botão Menu Mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-400 hover:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Abrir Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Menu Retrátil Mobile */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-b border-white/10 bg-[#020617]/95 px-6 py-4 space-y-3 backdrop-blur-2xl">
            <a
              href="#recursos"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-slate-300 hover:text-white py-2 font-medium"
            >
              Recursos do Sistema
            </a>
            <a
              href="#simulador"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-slate-300 hover:text-white py-2 font-medium"
            >
              Calculadora de Lucro (ROI)
            </a>
            <a
              href="#solucao"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-slate-300 hover:text-white py-2 font-medium"
            >
              Antes vs Depois
            </a>
            <a
              href="#depoimentos"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-slate-300 hover:text-white py-2 font-medium"
            >
              Depoimentos de Gestores
            </a>
            <a
              href="#precos"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-slate-300 hover:text-white py-2 font-medium"
            >
              Planos e Preços
            </a>
            <a
              href="#faq"
              onClick={() => setMobileMenuOpen(false)}
              className="block text-slate-300 hover:text-white py-2 font-medium"
            >
              Dúvidas Frequentes
            </a>
            <div className="pt-2 border-t border-white/10">
              <Link to="/sandbox" className="block w-full">
                <Button
                  variant="outline"
                  className="w-full justify-center border-slate-700 bg-slate-900 text-slate-200"
                >
                  <Play className="w-4 h-4 mr-2 text-blue-400 fill-current" />
                  Acessar Demonstração Gratuita
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-16 lg:pt-40 lg:pb-24 px-4 sm:px-6">
        {/* Glows Espaciais de Fundo */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[850px] h-[480px] bg-blue-600/12 blur-[150px] rounded-full pointer-events-none" />
        <div className="absolute top-1/3 left-1/4 w-full max-w-[500px] h-[350px] bg-cyan-500/10 blur-[130px] rounded-full pointer-events-none" />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 font-semibold text-xs sm:text-sm mb-6 sm:mb-8 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
            <span>Versão 2.0 • IA e Reconhecimento OCR de Placas</span>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 sm:mb-8 leading-[1.15]">
            A Gestão do seu Pátio com <br className="hidden sm:inline" />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-300 to-indigo-300">
              Precisão e Lucro Máximo.
            </span>
          </h1>

          <p className="text-base sm:text-lg lg:text-xl text-slate-400 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed">
            Elimine planilhas, comandas molhadas e furos no caixa. Controle o fluxo de veículos no
            Kanban em tempo real, identifique carros com câmera OCR e fidelize clientes com
            notificações automáticas via WhatsApp.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 max-w-md sm:max-w-none mx-auto">
            <a href="/login?trial=true" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto h-12 sm:h-14 px-8 text-base bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold shadow-xl shadow-blue-500/30 group"
              >
                Começar 7 Dias Grátis
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <Link to="/sandbox" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 sm:h-14 px-8 text-base bg-slate-900/60 border-slate-700 hover:bg-slate-800 text-slate-200 rounded-xl font-semibold backdrop-blur-md"
              >
                <Play className="w-4 h-4 mr-2 text-blue-400 fill-current" />
                Explorar Painel Interativo
              </Button>
            </Link>
          </div>

          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-400" /> Sem cartão no cadastro
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-400" /> Cancele quando quiser
            </span>
            <span className="hidden sm:flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-400" /> Suporte em português
            </span>
          </div>
        </div>

        {/* Hero Interactive App Mockup (Estilo Antigravity com Seletor de Abas) */}
        <div className="max-w-5xl mx-auto mt-12 sm:mt-16 relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 via-cyan-500/20 to-indigo-500/20 rounded-3xl blur-xl opacity-60"></div>

          <div className="relative bg-slate-950/90 border border-white/10 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden backdrop-blur-2xl">
            {/* Topbar Mac Chrome + Seletor de Módulos */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-3 border-b border-white/10 bg-slate-900/70 gap-3">
              <div className="flex items-center justify-between sm:justify-start gap-4">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                </div>
                <div className="text-[11px] font-mono text-slate-400 bg-slate-950/80 px-3 py-1 rounded-md border border-white/5 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                  app.autowash.pro/demo
                </div>
              </div>

              {/* Seletor de Abas Fluidas */}
              <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar py-1">
                <button
                  onClick={() => setHeroTab('ocr')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    heroTab === 'ocr'
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <Camera className="w-3.5 h-3.5" />
                  Leitura OCR
                </button>
                <button
                  onClick={() => setHeroTab('kanban')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    heroTab === 'kanban'
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <KanbanSquare className="w-3.5 h-3.5" />
                  Pátio Kanban
                </button>
                <button
                  onClick={() => setHeroTab('caixa')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    heroTab === 'caixa'
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Caixa Cego
                </button>
                <button
                  onClick={() => setHeroTab('whatsapp')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    heroTab === 'whatsapp'
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  WhatsApp
                </button>
              </div>
            </div>

            {/* Conteúdo Dinâmico da Aba Selecionada */}
            <div className="p-4 sm:p-8 min-h-[340px] sm:min-h-[400px] flex items-center justify-center bg-gradient-to-b from-slate-900/30 to-slate-950/80">
              {/* ABA 1: OCR */}
              {heroTab === 'ocr' && (
                <div className="w-full max-w-2xl space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-6 bg-slate-900/80 border border-blue-500/30 rounded-2xl shadow-xl">
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                      <div className="relative">
                        <div className="bg-slate-900 border-2 border-slate-600 px-3.5 py-1.5 rounded-md font-mono font-black text-lg tracking-widest text-white shadow-md flex items-center gap-2">
                          <span className="text-[9px] bg-blue-600 px-1 py-0.5 rounded text-white font-sans font-bold">
                            BR
                          </span>
                          BRA3E24
                        </div>
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-950 animate-ping"></div>
                      </div>
                      <div className="text-left">
                        <span className="text-[11px] text-emerald-400 font-bold uppercase tracking-wider block">
                          Reconhecimento Instantâneo (0.2s)
                        </span>
                        <h4 className="text-base sm:text-lg font-bold text-white">
                          Toyota Corolla Altis 2.0 • Preto
                        </h4>
                      </div>
                    </div>
                    <div className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-bold rounded-lg self-start sm:self-center">
                      Cliente VIP Identificado
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3 bg-slate-900/60 border border-white/5 rounded-xl text-left">
                      <span className="text-[10px] text-slate-500 block uppercase font-semibold">
                        Cliente Cadastrado
                      </span>
                      <span className="text-sm font-semibold text-slate-200">
                        Rodrigo Alcantara
                      </span>
                    </div>
                    <div className="p-3 bg-slate-900/60 border border-white/5 rounded-xl text-left">
                      <span className="text-[10px] text-slate-500 block uppercase font-semibold">
                        Último Serviço
                      </span>
                      <span className="text-sm font-semibold text-slate-200">
                        Higienização + Cera (22 dias atrás)
                      </span>
                    </div>
                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-left flex items-center justify-between">
                      <div>
                        <span className="text-[10px] text-blue-400 block uppercase font-bold">
                          Ação Sugerida
                        </span>
                        <span className="text-sm font-bold text-white">Abrir OS em 1 Clique</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-blue-400" />
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 2: KANBAN */}
              {heroTab === 'kanban' && (
                <div className="w-full space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    {/* Coluna 1 */}
                    <div className="bg-slate-900/70 border border-white/5 rounded-2xl p-3 sm:p-4 text-left">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                          Aguardando (3)
                        </span>
                        <span className="text-[10px] text-slate-500">Tempo médio: 8 min</span>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2.5 bg-slate-950 border border-white/5 rounded-xl text-xs font-medium text-slate-300 shadow">
                          <div className="font-bold text-white">
                            Compass Limited • Lavagem Simples
                          </div>
                          <span className="text-[10px] text-slate-400">
                            Chegou às 14:15 • Marcos
                          </span>
                        </div>
                        <div className="p-2.5 bg-slate-950 border border-white/5 rounded-xl text-xs font-medium text-slate-300 shadow">
                          <div className="font-bold text-white">BMW 320i • Polimento Comercial</div>
                          <span className="text-[10px] text-slate-400">
                            Chegou às 14:28 • Lucas
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Coluna 2 */}
                    <div className="bg-slate-900/70 border border-blue-500/20 rounded-2xl p-3 sm:p-4 text-left relative">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-blue-400 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-blue-400 animate-ping"></span>
                          Na Rampa / Lavagem (2)
                        </span>
                        <span className="text-[10px] text-blue-400/80">Em execução</span>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2.5 bg-blue-950/40 border border-blue-500/30 rounded-xl text-xs font-medium text-white shadow-lg">
                          <div className="font-bold text-blue-200">Hilux GR-S • Vitrificação</div>
                          <div className="flex justify-between items-center mt-1 text-[10px] text-blue-300">
                            <span>Rampa 02 (Gabriel)</span>
                            <span className="font-bold">75% concluído</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Coluna 3 */}
                    <div className="bg-slate-900/70 border border-white/5 rounded-2xl p-3 sm:p-4 text-left">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                          Pronto / Retirada (6)
                        </span>
                        <span className="text-[10px] text-emerald-400">Notificado</span>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2.5 bg-slate-950 border border-emerald-500/20 rounded-xl text-xs font-medium text-slate-300 shadow">
                          <div className="font-bold text-white">Porsche Macan • Detalhamento</div>
                          <span className="text-[10px] text-emerald-400 font-semibold">
                            Cliente avisado via WhatsApp
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA 3: CAIXA CEGO */}
              {heroTab === 'caixa' && (
                <div className="w-full max-w-2xl space-y-4">
                  <div className="p-4 sm:p-6 bg-slate-900/80 border border-rose-500/30 rounded-2xl text-left">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center shrink-0">
                        <ShieldAlert className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-white">
                          Auditoria de Fechamento de Caixa
                        </h4>
                        <p className="text-xs text-slate-400">
                          O operador declara o dinheiro físico sem saber o total esperado pelo
                          sistema.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-white/10 text-xs">
                      <div className="bg-slate-950 p-3 rounded-xl">
                        <span className="text-[10px] text-slate-500 uppercase font-semibold block">
                          Declarado pelo Operador
                        </span>
                        <span className="text-lg font-mono font-bold text-white">R$ 1.450,00</span>
                      </div>
                      <div className="bg-slate-950 p-3 rounded-xl border border-rose-500/20">
                        <span className="text-[10px] text-rose-400 uppercase font-bold block">
                          Esperado pelo Sistema
                        </span>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-mono font-bold text-rose-400">
                            R$ 1.500,00
                          </span>
                          <span className="text-[10px] bg-rose-500/10 text-rose-400 font-bold px-2 py-0.5 rounded">
                            Falta R$ 50
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 text-center">
                    Zero brechas para desvios. O gestor recebe relatório com histórico de operador e
                    horário.
                  </p>
                </div>
              )}

              {/* ABA 4: WHATSAPP */}
              {heroTab === 'whatsapp' && (
                <div className="w-full max-w-md bg-[#0b141a] border border-white/10 rounded-2xl p-4 sm:p-5 shadow-2xl text-left">
                  <div className="flex items-center gap-3 pb-3 border-b border-white/10">
                    <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center font-bold text-white text-xs">
                      AP
                    </div>
                    <div>
                      <h5 className="text-xs font-bold text-white">AutoWash Bot • Notificação</h5>
                      <span className="text-[10px] text-emerald-400">
                        Online • Confirmação Automática
                      </span>
                    </div>
                  </div>
                  <div className="my-4 p-3.5 bg-[#202c33] rounded-2xl rounded-tl-none max-w-[90%] text-xs space-y-2 text-slate-200">
                    <p className="leading-relaxed">
                      Olá <strong>Rodrigo</strong>! Seu <strong>Toyota Corolla</strong> acabou de
                      sair da rampa de acabamento e já está pronto para retirada no pátio! ✨🚗
                    </p>
                    <div className="p-2 bg-[#111b21] rounded-lg text-[11px] font-mono text-slate-300 border border-white/5">
                      <div>OS: #1042 • Lavagem Técnica + Cera</div>
                      <div className="text-emerald-400 font-bold">
                        Total: R$ 140,00 (Pago no PIX)
                      </div>
                    </div>
                    <span className="text-[9px] text-slate-400 block text-right">
                      15:42 • Enviado com sucesso
                    </span>
                  </div>
                  <div className="text-[10px] text-center text-slate-500">
                    Notificação 100% automatizada sem tocar no celular.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BARRA DE MÉTRICAS (Estatísticas no Fluxo Natural - Sem sobreposição) */}
      <section className="py-6 sm:py-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-slate-900/50 border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 backdrop-blur-xl shadow-xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
            <div className="text-center pt-3 sm:pt-0">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 block mb-1">
                10 seg
              </span>
              <span className="text-xs sm:text-sm text-slate-400 font-medium">
                Para dar entrada na OS com leitura OCR
              </span>
            </div>

            <div className="text-center pt-3 sm:pt-0 sm:pl-6">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-300 block mb-1">
                0%
              </span>
              <span className="text-xs sm:text-sm text-slate-400 font-medium">
                De furos no caixa com fechamento cego
              </span>
            </div>

            <div className="text-center pt-3 sm:pt-0 sm:pl-6">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-300 block mb-1">
                99.8%
              </span>
              <span className="text-xs sm:text-sm text-slate-400 font-medium">
                De precisão no reconhecimento de placas
              </span>
            </div>

            <div className="text-center pt-3 sm:pt-0 sm:pl-6">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-400 block mb-1">
                100% Web
              </span>
              <span className="text-xs sm:text-sm text-slate-400 font-medium">
                Celular, Tablet ou PC sem instalação
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Grid: Recursos com SpotlightCard */}
      <section id="recursos" className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-4">
            Tecnologia de Ponta
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4 text-white">
            Engenharia pensada para o Chão de Pátio
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            Cada recurso foi desenhado ouvindo quem vive a correria de gerenciar lava-rápido,
            estética automotiva e centro de detalhamento.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: OCR (Destaque - 2 cols) */}
          <SpotlightCard className="md:col-span-2 p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Camera className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                Identificação OCR de Placas por Câmera
              </h3>
              <p className="text-slate-400 text-sm sm:text-base leading-relaxed mb-6 max-w-xl">
                Agilize o pátio e elimine erros humanos. Ao apontar o celular na placa Mercosul ou
                padrão antigo, o sistema extrai o texto, busca o histórico do proprietário e abre a
                OS pronta para escolha dos serviços.
              </p>
            </div>
            <div className="bg-slate-950/70 border border-white/5 rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="px-2.5 py-1 bg-slate-900 border border-slate-700 rounded text-xs font-mono font-bold text-white">
                  BRA2E19
                </div>
                <span className="text-xs text-slate-300 font-medium">
                  Jeep Compass • Preto Metálico
                </span>
              </div>
              <span className="text-[10px] bg-emerald-500/10 text-emerald-400 font-bold px-2 py-0.5 rounded border border-emerald-500/20">
                100% Automático
              </span>
            </div>
          </SpotlightCard>

          {/* Card 2: Caixa Cego */}
          <SpotlightCard className="p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Caixa Cego Antifraude</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                O operador não tem acesso ao saldo teórico ao fechar o caixa. Divergências em
                dinheiro ou PIX geram relatórios de auditoria imediatos.
              </p>
            </div>
            <div className="bg-rose-500/5 border border-rose-500/15 rounded-xl p-3 text-xs text-rose-300 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 shrink-0 text-rose-400" />
              Auditoria blindada e sem alterações manuais.
            </div>
          </SpotlightCard>

          {/* Card 3: Kanban em Tempo Real */}
          <SpotlightCard className="p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-2xl flex items-center justify-center mb-6">
                <KanbanSquare className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Fluxo Kanban Visual</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Acompanhe o pátio inteiro em uma única tela. Arraste serviços entre Aguardando,
                Lavagem, Polimento e Pronto.
              </p>
            </div>
            <div className="flex gap-2">
              <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-1 rounded">
                Fila de espera
              </span>
              <span className="text-[10px] bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                Na rampa
              </span>
              <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded">
                Pronto
              </span>
            </div>
          </SpotlightCard>

          {/* Card 4: Comissões Automáticas */}
          <SpotlightCard className="p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Comissões Transparentes</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Diga adeus às brigas no sábado. Comissões calculadas por serviço ou rateadas entre
                lavador e polidor no momento do checkout.
              </p>
            </div>
            <div className="bg-slate-950 p-3 rounded-xl border border-white/5 text-xs text-slate-300 flex justify-between">
              <span>Marcos (Lavador)</span>
              <span className="text-emerald-400 font-bold">+R$ 22,00 (15%)</span>
            </div>
          </SpotlightCard>

          {/* Card 5: Mobile & Responsividade */}
          <SpotlightCard className="p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="w-12 h-12 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-2xl flex items-center justify-center mb-6">
                <Smartphone className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Operação na Rampa</h3>
              <p className="text-slate-400 text-sm leading-relaxed mb-6">
                Interface leve e fluida para o vistoriador usar direto no smartphone com uma mão,
                mesmo com a mão molhada.
              </p>
            </div>
            <div className="flex items-center justify-around text-xs text-slate-400 border-t border-white/5 pt-3">
              <span className="flex items-center gap-1">
                <Monitor className="w-4 h-4 text-blue-400" /> Desktop
              </span>
              <span className="flex items-center gap-1">
                <Tablet className="w-4 h-4 text-cyan-400" /> Tablet
              </span>
              <span className="flex items-center gap-1">
                <Smartphone className="w-4 h-4 text-emerald-400" /> Celular
              </span>
            </div>
          </SpotlightCard>
        </div>
      </section>

      {/* CALCULADORA DE ROI E LUCRO DO PÁTIO (Inspirada no TonsuraPro) */}
      <section
        id="simulador"
        className="py-16 sm:py-24 bg-slate-900/30 border-y border-white/5 px-4 sm:px-6"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3">
              Simulador Financeiro
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
              Quanto o AutoWash Pro Coloca a Mais no Seu Bolso?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
              Descubra o faturamento que você recupera mensalmente ao blindar o caixa e cortar a
              ociosidade do pátio.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center max-w-5xl mx-auto">
            {/* Controles / Sliders */}
            <div className="lg:col-span-7 space-y-6 bg-slate-950/70 border border-white/10 rounded-3xl p-6 sm:p-8 backdrop-blur-xl">
              {/* Slider 1: Carros por Dia */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-slate-200">
                    Veículos atendidos por dia:
                  </label>
                  <span className="text-lg font-bold text-blue-400 font-mono">
                    {carsPerDay} carros/dia
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="60"
                  value={carsPerDay}
                  onChange={(e) => setCarsPerDay(Number(e.target.value))}
                  className="w-full accent-blue-500 cursor-pointer h-2 bg-slate-800 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                  <span>5 carros</span>
                  <span>30 carros</span>
                  <span>60 carros</span>
                </div>
              </div>

              {/* Slider 2: Ticket Médio */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-slate-200">
                    Ticket médio por serviço:
                  </label>
                  <span className="text-lg font-bold text-emerald-400 font-mono">
                    R$ {avgTicket},00
                  </span>
                </div>
                <input
                  type="range"
                  min="30"
                  max="300"
                  step="5"
                  value={avgTicket}
                  onChange={(e) => setAvgTicket(Number(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer h-2 bg-slate-800 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                  <span>R$ 30 (Ducha simples)</span>
                  <span>R$ 150 (Higienização)</span>
                  <span>R$ 300 (Detalhamento)</span>
                </div>
              </div>

              {/* Slider 3: Dias de Funcionamento */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-slate-200">
                    Dias de pátio aberto no mês:
                  </label>
                  <span className="text-lg font-bold text-cyan-400 font-mono">
                    {workingDays} dias
                  </span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="30"
                  value={workingDays}
                  onChange={(e) => setWorkingDays(Number(e.target.value))}
                  className="w-full accent-cyan-500 cursor-pointer h-2 bg-slate-800 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                  <span>20 dias (Seg-Sex)</span>
                  <span>26 dias (Seg-Sáb)</span>
                  <span>30 dias (Direto)</span>
                </div>
              </div>

              <div className="p-4 bg-slate-900/50 rounded-2xl border border-white/5 text-xs text-slate-400 flex items-center gap-3">
                <Clock className="w-5 h-5 text-blue-400 shrink-0" />
                <span>
                  Economia estimada de{' '}
                  <strong>{roiData.hoursSavedMonth} horas de retrabalho</strong> por mês na recepção
                  e fechamento.
                </span>
              </div>
            </div>

            {/* Resultado do ROI em SpotlightCard */}
            <div className="lg:col-span-5">
              <SpotlightCard className="p-6 sm:p-8 border-blue-500/30 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-emerald-500 text-slate-950 font-bold text-[10px] px-3 py-1 rounded-bl-xl uppercase tracking-wider">
                  Impacto Comprovado
                </div>

                <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold block mb-2">
                  Faturamento Adicional Recuperado
                </span>

                <div className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-2 text-emerald-400">
                  +R${' '}
                  {roiData.recoveredRevenue.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
                <span className="text-xs text-slate-400 block mb-6">
                  estimados a mais todo mês no seu caixa
                </span>

                <div className="space-y-3 text-left border-t border-white/10 pt-4 mb-6 text-xs text-slate-300">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Faturamento Bruto Mensal:</span>
                    <span className="font-bold text-white">
                      R$ {roiData.grossRevenue.toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total de Veículos no Mês:</span>
                    <span className="font-bold text-white">{roiData.monthlyCars} carros</span>
                  </div>
                  <div className="flex justify-between text-emerald-400 font-bold">
                    <span>Retorno do Investimento:</span>
                    <span>O plano se paga em 2 dias</span>
                  </div>
                </div>

                <a href="/login?trial=true" className="w-full block">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold h-12 rounded-xl text-sm shadow-lg shadow-emerald-600/25">
                    Garantir Esse Resultado Agora
                  </Button>
                </a>
              </SpotlightCard>
            </div>
          </div>
        </div>
      </section>

      {/* Comparativo: Sem AutoWash Pro vs Com AutoWash Pro */}
      <section id="solucao" className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            O Custo Invisível da Gestão no Caderno
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            Veja a diferença brutal entre operar no modo manual e adotar o padrão profissional de
            alta performance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Lado Negativo / Caderno */}
          <div className="bg-slate-950/60 border border-rose-500/20 rounded-3xl p-6 sm:p-8 relative">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center font-bold">
                ✕
              </div>
              <h3 className="text-xl font-bold text-rose-300">Sem AutoWash Pro (Gestão Amadora)</h3>
            </div>
            <div className="space-y-4 text-sm text-slate-300">
              <div className="flex items-start gap-3 bg-slate-900/40 p-3.5 rounded-xl border border-white/5">
                <span className="text-rose-400 font-bold">✕</span>
                <span>Comandas de papel rasgam, molham e se perdem na área de lavagem.</span>
              </div>
              <div className="flex items-start gap-3 bg-slate-900/40 p-3.5 rounded-xl border border-white/5">
                <span className="text-rose-400 font-bold">✕</span>
                <span>
                  Serviços adicionais (cera, vitrificação) esquecidos na hora de cobrar no balcão.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-slate-900/40 p-3.5 rounded-xl border border-white/5">
                <span className="text-rose-400 font-bold">✕</span>
                <span>
                  Furos frequentes no fechamento do caixa sem saber quem errou ou onde sumiu.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-slate-900/40 p-3.5 rounded-xl border border-white/5">
                <span className="text-rose-400 font-bold">✕</span>
                <span>Lavadores insatisfeitos com conferência manual de comissão no sábado.</span>
              </div>
            </div>
          </div>

          {/* Lado Positivo / AutoWash Pro */}
          <div className="bg-slate-950/80 border border-blue-500/40 rounded-3xl p-6 sm:p-8 relative shadow-2xl shadow-blue-500/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-bold">
                ✓
              </div>
              <h3 className="text-xl font-bold text-blue-300">
                Com AutoWash Pro (Padrão de Elite)
              </h3>
            </div>
            <div className="space-y-4 text-sm text-slate-200">
              <div className="flex items-start gap-3 bg-blue-950/20 p-3.5 rounded-xl border border-blue-500/20">
                <Check className="w-5 h-5 text-blue-400 shrink-0" />
                <span>
                  OS digital completa em 10 segundos ao apontar a câmera na placa do carro.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-blue-950/20 p-3.5 rounded-xl border border-blue-500/20">
                <Check className="w-5 h-5 text-blue-400 shrink-0" />
                <span>
                  Caixa Cego Antifraude com auditoria implacável de cada centavo em dinheiro e PIX.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-blue-950/20 p-3.5 rounded-xl border border-blue-500/20">
                <Check className="w-5 h-5 text-blue-400 shrink-0" />
                <span>
                  Pátio organizado em Kanban com visão clara de gargalos e tempo de execução.
                </span>
              </div>
              <div className="flex items-start gap-3 bg-blue-950/20 p-3.5 rounded-xl border border-blue-500/20">
                <Check className="w-5 h-5 text-blue-400 shrink-0" />
                <span>
                  Notificações automáticas no WhatsApp avisando que o carro está pronto para
                  retirada.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Depoimentos de Clientes (Prova Social) */}
      <section
        id="depoimentos"
        className="py-16 sm:py-24 bg-slate-900/20 max-w-7xl mx-auto px-4 sm:px-6"
      >
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-3">
            Histórias Reais
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Quem Usa, Não Volta para o Papel
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
            Depoimentos de donos de centros automotivos e estéticas que multiplicaram o faturamento
            com o AutoWash Pro.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {TESTIMONIALS.map((t, idx) => (
            <SpotlightCard key={idx} className="p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-1 text-amber-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <span className="text-xs font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-0.5 rounded-full">
                    {t.metric}
                  </span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed italic mb-6">"{t.quote}"</p>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-cyan-400 flex items-center justify-center font-bold text-white text-xs">
                  {t.initials}
                </div>
                <div className="text-left">
                  <h5 className="text-sm font-bold text-white">{t.name}</h5>
                  <p className="text-xs text-slate-400">
                    {t.role} • {t.shop}
                  </p>
                </div>
              </div>
            </SpotlightCard>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="py-16 sm:py-24 max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-4">
            Investimento Transparente
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Planos sem Letras Miúdas
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto">
            Sem fidelidade. Comece agora no plano mensal ou garanta 20% de desconto no faturamento
            anual.
          </p>

          {/* Toggle Mensal / Anual */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span
              className={`text-sm font-semibold ${billingPeriod === 'monthly' ? 'text-white' : 'text-slate-500'}`}
            >
              Mensal
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              className="w-14 h-7 rounded-full bg-slate-800 border border-slate-600 p-1 flex items-center transition-colors"
              aria-label="Alternar plano anual"
            >
              <div
                className={`w-5 h-5 rounded-full bg-blue-500 transition-transform duration-300 ${billingPeriod === 'yearly' ? 'translate-x-7 bg-cyan-400' : ''}`}
              />
            </button>
            <div className="flex items-center gap-2">
              <span
                className={`text-sm font-semibold ${billingPeriod === 'yearly' ? 'text-white' : 'text-slate-500'}`}
              >
                Anual
              </span>
              <span className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-bold px-2 py-0.5 rounded-full">
                Economize 20%
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan, index) => {
            const isHighlight = index === 1 // Destacar o plano PRO
            const displayPrice = getDisplayPrice(plan.price)

            return (
              <SpotlightCard
                key={plan.id}
                className={`p-6 sm:p-8 flex flex-col justify-between ${
                  isHighlight
                    ? 'border-blue-500/60 bg-gradient-to-b from-blue-950/30 via-slate-900/60 to-slate-950 shadow-2xl shadow-blue-500/20 md:-translate-y-4'
                    : 'border-white/10'
                }`}
              >
                <div>
                  {isHighlight && (
                    <div className="bg-blue-600 text-white text-[10px] font-extrabold uppercase tracking-wider py-1 px-3 rounded-full inline-block mb-4">
                      Mais Escolhido pelas Estéticas
                    </div>
                  )}

                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-slate-400 text-xs sm:text-sm min-h-[36px] mb-6">
                    {plan.description}
                  </p>

                  <div className="mb-6 pb-6 border-b border-white/10">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl sm:text-5xl font-extrabold text-white">
                        R$ {displayPrice}
                      </span>
                      <span className="text-slate-400 text-sm">/mês</span>
                    </div>
                    <p className="text-emerald-400 text-xs font-semibold mt-1.5">
                      ou R$ {(displayPrice * 0.9).toFixed(2).replace('.', ',')} no PIX (10% off)
                    </p>
                    {billingPeriod === 'yearly' && (
                      <span className="text-[10px] text-slate-500 block mt-1">
                        Cobrado anualmente
                      </span>
                    )}
                  </div>

                  <div className="space-y-3 mb-8">
                    {plan.features_json.map((feature, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-300"
                      >
                        <CheckCircle2
                          className={`w-4 h-4 shrink-0 mt-0.5 ${isHighlight ? 'text-blue-400' : 'text-slate-400'}`}
                        />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2.5 pt-4">
                  <a href={`/login?plan=${plan.id}`} className="w-full block">
                    <Button
                      className={`w-full h-12 rounded-xl font-bold text-sm ${
                        isHighlight
                          ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                          : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                      }`}
                    >
                      Assinar Plano {plan.name}
                    </Button>
                  </a>
                  <a href="/login?trial=true" className="w-full block">
                    <Button
                      variant="ghost"
                      className="w-full h-10 text-xs text-slate-400 hover:text-white"
                    >
                      Testar 7 Dias Grátis
                    </Button>
                  </a>
                </div>
              </SpotlightCard>
            )
          })}
        </div>
      </section>

      {/* Seção FAQ com Accordion */}
      <section id="faq" className="py-16 sm:py-24 max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider mb-3">
            Tire Suas Dúvidas
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Perguntas Frequentes
          </h2>
          <p className="text-slate-400 text-sm sm:text-base">
            Tudo o que você precisa saber antes de transformar a operação do seu pátio.
          </p>
        </div>

        <div className="space-y-4">
          {FAQ_ITEMS.map((item, idx) => {
            const isOpen = openFaq === idx
            return (
              <div
                key={idx}
                className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(isOpen ? null : idx)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 font-bold text-sm sm:text-base text-white hover:text-blue-400 transition-colors"
                >
                  <span>{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-400' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-6 pb-6 text-sm text-slate-400 leading-relaxed border-t border-white/5 pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* Banner Final de Conversão (Pre-footer) */}
      <section className="py-16 sm:py-24 max-w-6xl mx-auto px-4 sm:px-6">
        <SpotlightCard className="p-8 sm:p-14 text-center border-blue-500/40 bg-gradient-to-b from-blue-950/40 to-slate-950 shadow-2xl relative overflow-hidden">
          <div className="max-w-2xl mx-auto relative z-10">
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-6">
              Pronto para colocar ordem e lucro no seu pátio?
            </h2>
            <p className="text-slate-300 text-base sm:text-lg mb-8 leading-relaxed">
              Junte-se a dezenas de estéticas automotivas que abandonaram as pranchetas e hoje
              operam com leitura de placa e caixa 100% blindado.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/login?trial=true" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto h-14 px-8 text-base bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-xl shadow-blue-500/30"
                >
                  Iniciar Teste Grátis (7 Dias)
                </Button>
              </a>
              <Link to="/sandbox" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto h-14 px-8 text-base border-slate-700 bg-slate-900/60 text-slate-200 rounded-xl"
                >
                  Acessar Demonstração
                </Button>
              </Link>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Leva menos de 2 minutos para configurar. Sem cartão no cadastro.
            </p>
          </div>
        </SpotlightCard>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#020617] py-12 px-4 sm:px-6 text-center text-slate-500 text-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="/logo.webp"
              alt="AutoWash Pro Logo"
              className="w-6 h-6 object-contain opacity-70"
            />
            <span className="font-bold text-slate-300">AutoWash Pro</span>
          </div>
          <p className="max-w-2xl mx-auto mb-6 text-xs text-slate-500 leading-relaxed">
            Plataforma de gestão integrada para lava-rápidos, centros de estética automotiva e
            estúdios de detalhamento. Controle de fluxo, OCR de placas e blindagem financeira em
            tempo real.
          </p>
          <div className="flex justify-center gap-4 text-xs mb-6">
            <a
              href="/privacidade.html"
              className="text-blue-500 hover:text-blue-400 transition-colors"
            >
              Política de Privacidade
            </a>
            <span className="text-slate-800">•</span>
            <a href="/termos.html" className="text-blue-500 hover:text-blue-400 transition-colors">
              Termos de Uso
            </a>
          </div>
          <p className="text-xs text-slate-600">
            © {new Date().getFullYear()} AutoWash Pro. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  )
}
